// src/services/databaseRebuildService.ts
import { authTokenManager } from '../utils/authTokenManager';
import transformers from './dataPersistence/transformers';
import { Message, Location } from '../types/chat';
import { cityContext } from './cityContext';

// Define result interface
export interface RebuildResult {
  messages: Message[];
  locations: Location[];
  selectedLocationId?: string;
  weatherLocation?: string;
  scrollPosition?: number;
  messageLocationMap: { [messageId: string]: Location[] };
  debug?: any;
}

// Helper to validate sender
function validateSender(sender: string): "bot" | "user" {
    return sender === "bot" ? "bot" : "user";
  }

// Add these interfaces at the top of databaseRebuildService.ts
interface ConversationElement {
    element: {
      type: 'message' | 'locationCard' | 'weatherWidget' | 'imageMessage';
      messageId?: string;
    };
    data: any;
  }

// Main rebuild function
export async function fetchAndRebuildFromDatabase(): Promise<RebuildResult> {
    
  // Check authentication
  if (!authTokenManager.isAuthenticated()) {
    throw new Error('Not authenticated - login required');
  }
  
  const debug: any = {
    fetchStartTime: new Date().toISOString(),
    steps: []
  };
  
  try {
    // 1. Fetch all required data
    //debug.steps.push({ step: 'Fetching all data miyaaaaannnnnn', time: new Date().toISOString() });
    //console.log('Step 1 - Fetching all data', new Date().toISOString());
    const [conversationData, locationsData, weatherData, mapStateData, imagesData] = await Promise.all([
      fetchConversationData(),
      fetchLocationData(),
      fetchWeatherData(),
      fetchMapStateData(),
      fetchImageData()
    ]);
    
    // 2. Transform API data to client format
    //debug.steps.push({ step: 'Transforming data', time: new Date().toISOString() });
    //console.log('Step 2 - Transforming data', new Date().toISOString());
    const clientConversation = transformers.conversationApiToClient(conversationData);
    const clientLocations = transformers.locationsApiToClient(locationsData);
    const clientWeather = transformers.weatherWidgetsApiToClient(weatherData);
    const clientMapState = transformers.mapStateApiToClient(mapStateData);
    const clientImages = Array.isArray(imagesData) ? imagesData : [imagesData];

    //console.log('[DatabaseRebuildService] clientWeather:', clientWeather);

    //console.log(' [DatabaseRebuildService] clientWeather:', locationsData);
    /* console.log('[DatabaseRebuild] Client data:', {
      conversation: clientConversation,
      locations: clientLocations,
      weather: clientWeather,
      mapState: clientMapState,
      images: clientImages
    }); */
    
    // 3. Process data to build conversation elements
    //debug.steps.push({ step: 'Building conversation elements', time: new Date().toISOString() });
    //console.log('Step 3 - Building conversation elements', new Date().toISOString());
    const conversationElements = buildConversationElements(
      clientConversation,
      clientLocations,
      clientWeather,
      clientImages
    );
    
    // 4. Rebuild messages, locations, and relationships
    //debug.steps.push({ step: 'Rebuilding UI data', time: new Date().toISOString() });
    //console.log('Step 4 - Rebuilding UI data', new Date().toISOString());

    //console.log('Step 5 - Getting data to rebuild UI:', conversationElements);
    const { messages, locations, messageLocationMap, weatherLocation } = processConversationElements(conversationElements);
    
    /* console.log('[DatabaseRebuild] Message-location mapping:', {
      messageCount: messages.length,
      locationCount: locations.length,
      mappingCount: Object.keys(messageLocationMap).length,
      mappingSample: Object.entries(messageLocationMap).slice(0, 2)
    }); */

    setCityContextFromLocations(
      locations, 
      clientMapState?.selectedLocationId, 
      clientConversation,
      messageLocationMap
    );

    // 5. Return rebuilt data
    return {
      messages, // From processConversationElements function
      locations, // From processConversationElements function
      selectedLocationId: clientMapState?.selectedLocationId,
      weatherLocation: weatherLocation ?? undefined,
      scrollPosition: clientConversation?.lastScrollPosition,
      messageLocationMap, // This line is already included
      debug
    };
  } catch (error) {
    console.error('[DatabaseRebuildService] Error:', error);
    throw error;
  }
}

// Helper functions for data fetching
async function fetchConversationData() {
  const response = await fetch('/api/user-data/conversation', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authTokenManager.getToken()}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch conversation: ${response.status}`);
  }
  
  return response.json();
}

async function fetchLocationData() {
  const response = await fetch('/api/user-data/locations', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authTokenManager.getToken()}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch locations: ${response.status}`);
  }
  
  return response.json();
}

async function fetchWeatherData() {
  const response = await fetch('/api/user-data/weather', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authTokenManager.getToken()}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch weather: ${response.status}`);
  }
  
  //console.log('[ConversationService] [DatabaseRebuildService] Fetching weather data from API ', response);

  return response.json();
}

async function fetchMapStateData() {
  const response = await fetch('/api/user-data/mapstate', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authTokenManager.getToken()}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch map state: ${response.status}`);
  }
  
  return response.json();
}

async function fetchImageData() {
  const response = await fetch('/api/user-data/images', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${authTokenManager.getToken()}`,
      'Content-Type': 'application/json'
    }
  });

  //console.log('[ConversationService] [DatabaseRebuildService] Fetching image data from API ', response);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch images: ${response.status}`);
  }
  
  return response.json();
}

// Helper function to build conversation elements from client data

  function setCityContextFromLocations(
    locations: Location[], 
    selectedLocationId?: string, 
    clientConversation?: any,
    messageLocationMap?: { [messageId: string]: Location[] }
  ): void {
    let cityToSet: string | undefined;
    
    // Priority 1: Check if we have a stored city context in conversation data
    if (clientConversation && clientConversation.cityContext) {
      cityToSet = clientConversation.cityContext;
      //console.log(`[DatabaseRebuild] Setting city context from stored value: ${cityToSet}`);
    }
    
    // Priority 2: Find city from the most recent message with locations
    // This is more accurate than just using the last location
    if (!cityToSet && messageLocationMap && Object.keys(messageLocationMap).length > 0) {
      // Sort message IDs numerically in descending order (most recent first)
      const messageIds = Object.keys(messageLocationMap).sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        return (isNaN(numA) || isNaN(numB)) ? a.localeCompare(b) : numB - numA;
      });
      
      // Get the most recent message ID with locations
      const mostRecentMessageId = messageIds[0];
      
      if (mostRecentMessageId && messageLocationMap[mostRecentMessageId]?.length > 0) {
        const mostRecentLocation = messageLocationMap[mostRecentMessageId][0];
        if (mostRecentLocation.city) {
          cityToSet = mostRecentLocation.city;
          //console.log(`[DatabaseRebuild] Setting city context from most recent message location: ${cityToSet}`);
        }
      }
    }
    
    // Priority 3: Use the selected location if available
    if (!cityToSet && selectedLocationId && locations && locations.length > 0) {
      const selectedLocation = locations.find(loc => loc.id === selectedLocationId);
      if (selectedLocation && selectedLocation.city) {
        cityToSet = selectedLocation.city;
        //console.log(`[DatabaseRebuild] Setting city context from selected location: ${cityToSet}`);
      }
    }
    
    // Priority 4: Fall back to the last location in the array
    if (!cityToSet && locations && locations.length > 0) {
      const lastLocation = locations[locations.length - 1];
      if (lastLocation && lastLocation.city) {
        cityToSet = lastLocation.city;
        //console.log(`[DatabaseRebuild] Setting city context from last location in array: ${cityToSet}`);
      }
    }
    
    // If we have a city to set, update the city context
    if (cityToSet) {
      cityContext.setCurrentCity(cityToSet);
      //console.log(`[DatabaseRebuild] City context set to: ${cityToSet}`);
    } else {
      //console.log('[DatabaseRebuild] Could not determine city from any available sources');
    }
  }

  function buildConversationElements(
    clientConversation: any, 
    clientLocations: any[], 
    clientWeather: any[], 
    clientImages: any[]
  ): ConversationElement[] {

  const conversationElements: ConversationElement[] = [];
  const processedLocationIds = new Set(); // Track processed locations

  if (clientConversation && clientConversation.sequence) {
    //console.log('[DATABASE REBUILD SEQUENCE] :', clientConversation.sequence);
    for (const item of clientConversation.sequence) {
      //console.log('[DATABASE REBUILD ITEM] - ', item.type);
      if (item.type === 'message') {
        try {
          const elementData = JSON.parse(item.elementId);
          conversationElements.push({
            element: { type: 'message' },
            data: elementData
          });
        } catch (e) {
          //console.warn('[DatabaseRebuildService] Failed to parse message data:', item);
        }
      } 
      else if (item.type === 'locationCard') {
        //console.log('[DATABASE REBUILD LOCATION] - ', item);
        
        /* console.log('[Location Matching] Looking for location:', {
          elementId: item.elementId,
          locationName: item.locationName,
          locationCity: item.locationCity,
          locationCountry: item.locationCountry
        });

        console.log('[Location Matching] Available locations:', clientLocations.map(loc => ({
          id: loc.id,
          name: loc.name,
          city: loc.city,
          country: loc.country
        }))); */

        const locationCard = clientLocations.find(loc => {
          // 1. ID match (highest priority)
          const matchById = loc.id === item.elementId;
          
          // 2. Name + Country match (ignoring city for flexibility)
          const matchByNameCountry = loc.name === item.locationName && 
                                    loc.country === item.locationCountry;
          
          // 3. Full details match with flexible city handling
          const cityMatch = loc.city === item.locationCity || 
                            loc.city === 'Unknown' || 
                            item.locationCity === 'Unknown' ||
                            (!loc.city && !item.locationCity);
          
          const matchByDetails = loc.name === item.locationName && 
                                cityMatch && 
                                loc.country === item.locationCountry;
          
          if (matchById || matchByNameCountry || matchByDetails) {
            /* console.log('[Location Matching] FOUND MATCH:', {
              matchType: matchById ? 'ID' : (matchByNameCountry ? 'Name+Country' : 'Details'),
              location: loc,
              cityComparison: {
                stored: loc.city,
                searching: item.locationCity,
                matched: cityMatch
              }
            }); */
          }
          
          return matchById || matchByNameCountry || matchByDetails;
        });

        if (!locationCard) {
          /* console.log('[Location Matching] NO MATCH FOUND for:', {
            elementId: item.elementId,
            name: item.locationName,
            city: item.locationCity,
            country: item.locationCountry
          }); */
        }

        //console.log('[DATABASE REBUILD LOCATION CARD] - ', locationCard);

        if (locationCard) {
          const locationKey = `${locationCard.name}_${locationCard.city}_${locationCard.country}`;

          // Only add if not already processed
          if (!processedLocationIds.has(locationKey)) {
            processedLocationIds.add(locationKey);
            conversationElements.push({
              element: { 
                type: 'locationCard',
                messageId: item.messageId 
              },
              data: locationCard
            });
          } else {
            //console.log(`[DatabaseRebuild] Skipping duplicate location: ${locationKey}`);
          }
        }
      }
      else if (item.type === 'weatherWidget-unused') {
        //console.log('[DATABASE REBUILD WEATHER WIDGET] ------------------ ', item);
        const weatherWidget = clientWeather.find(w => w.id === item.elementId);
        if (weatherWidget) {
          //console.log('[DATABASE REBUILD WEATHER WIDGET] ------------------ ', weatherWidget);
          conversationElements.push({
            element: { 
              type: 'weatherWidget',
              messageId: item.messageId 
            },
            data: weatherWidget
          });
        }
      }
      else if (item.type === 'weatherWidget') {
        // Find the message that triggered this weather widget
        const triggerMessage = conversationElements
          .slice(0, conversationElements.length)
          .reverse()
          .find(el => el.element.type === 'message' && el.data?.id === item.messageId);
        
        let location = 'Unknown';
        
        if (triggerMessage?.data?.content) {
          // Extract location from "Let me check the current weather in [location]..."
          const match = triggerMessage.data.content.match(/weather in (.+?)\.\.\./i);
          if (match && match[1]) {
            location = match[1].trim();
          }
        }
        
        // Create minimal weather widget
        const weatherWidget = {
          id: item.elementId,
          location: location,
          messageId: item.messageId
        };
        
        //console.log('[DATABASE REBUILD WEATHER WIDGET] Created minimal widget NOW :', weatherWidget);
        conversationElements.push({
          element: { 
            type: 'weatherWidget',
            messageId: item.messageId 
          },
          data: weatherWidget
        });
      } 
      else if (item.type === 'imageMessage') {
        // Match based on filename in the URL instead of ID
        const imageMessage = clientImages.find(img => 
          img.imageUrl && img.imageUrl.includes(item.elementId)
        );

        if (imageMessage) {
          //console.log('[DatabaseRebuildService] Found image match:', imageMessage);
          conversationElements.push({
            element: { 
              type: 'imageMessage'
            },
            data: {
              id: imageMessage.id,
              content: imageMessage.content || '',
              sender: imageMessage.sender,
              timestamp: imageMessage.timestamp,
              type: 'image',
              imageUrl: imageMessage.imageUrl
            }
          });
        }
      }
    }
  }
  //console.log('Step 6 - Getting Conversation Objects:', conversationElements);
  return conversationElements;
}  

// Process conversation elements into UI data
function processConversationElements(conversationElements: ConversationElement[]) {
    const rebuiltMessages: any[] = [];
    const rebuiltLocations: Location[] = [];
    const messageLocationMap: { [messageId: string]: Location[] } = {}; // Add index signature
    let weatherLocation: string | null = null;
  
    //console.log('Processing conversation elements:', conversationElements);
  // First pass: Process messages
  conversationElements.forEach(({ element, data }) => {
    if (element.type === 'message' && data) {
        rebuiltMessages.push({
          id: data.id,
          content: data.content,
          sender: validateSender(data.sender), // Validate sender
          timestamp: new Date(data.timestamp),
          type: data.type,
          imageUrl: data.imageUrl
        });
      } else if (element.type === 'imageMessage' && data) {
        rebuiltMessages.push({
          id: data.id,
          content: data.content || '',
          sender: validateSender(data.sender), // Validate sender
          timestamp: new Date(data.timestamp),
          type: 'image',
          imageUrl: data.imageUrl
        });
      }
  });
  
  // Second pass: Process locations
  conversationElements.forEach(({ element, data }) => {
    if (element.type === 'locationCard' && data && element.messageId) {
      rebuiltLocations.push(data);
      
      if (!messageLocationMap[element.messageId]) {
        messageLocationMap[element.messageId] = [];
      }
      messageLocationMap[element.messageId].push(data);
    }
  });

  /* console.log('[DatabaseRebuild] Built location mapping:', 
    Object.keys(messageLocationMap).map(msgId => 
      `Message ${msgId} has ${messageLocationMap[msgId].length} locations`
    )
  ); */
  
  
  // Third pass: Check for weather widgets

  /* conversationElements.forEach(({ element, data }) => {
     //console.log('[DatabaseRebuild] Processing element: ----------------------- ', element);  
    if (element.type === 'weatherWidget') {
      weatherLocation = data.location;                                         
    }                                                                          
  }); */ 

  /* conversationElements.forEach(({ element, data }) => {
    if (element.type === 'weatherWidget' && data) {
      weatherLocation = data.location;
      
      // Add weather to message's _extractedData
      if (element.messageId) {
        const msgIndex = rebuiltMessages.findIndex(msg => msg.id === element.messageId);
        if (msgIndex >= 0) {
          rebuiltMessages[msgIndex]._extractedData = {
            ...rebuiltMessages[msgIndex]._extractedData,
            weather: { location: data.location }
          };
        }
      }
    }
  }); */

  // Third pass: Check for weather widgets
  conversationElements.forEach(({ element, data }) => {
    if (element.type === 'weatherWidget' && data) {
      weatherLocation = data.location;
    }
   //console.log('[DatabaseRebuild] Processing element:', element);
   if (element.type === 'weatherWidget' && data) {

      //console.log('[DatabaseRebuild] Processing weather widget:', element, data);

      weatherLocation = data.location;

      //console.log('[DatabaseRebuild] Weather location set to:', weatherLocation);
      
      // Add weather data to the message
      if (element.messageId) {
        const messageIndex = rebuiltMessages.findIndex(msg => msg.id === element.messageId);
        if (messageIndex >= 0) {
          rebuiltMessages[messageIndex]._extractedData = {
            ...rebuiltMessages[messageIndex]._extractedData,
            weather: { location: data.location }
          };
        }
      }
    }
  });
  
  // Add location information to messages
  const enhancedMessages = rebuiltMessages.map(msg => {
    const locations = messageLocationMap[msg.id] || [];
    return {
      ...msg,
      _associatedLocations: locations
    };
  });
  
  return {
    messages: enhancedMessages,
    locations: rebuiltLocations,
    messageLocationMap,
    weatherLocation
  };
}